<script lang="ts">
  import {metadata, preview, user} from '@/store';
  import type {BrandKit} from '@/types/user';
  import MediaService from '@/services/media';
  import {onDestroy, onMount} from 'svelte';
  import Icon from '../icon/Icon.svelte';
  import {createFileUploadDialog} from '../../utils/common';
  import Swal from 'sweetalert2';
  import {httpClient} from '../../utils/http-client';
  import type {UploadActions, UploadPayload} from '../../types/upload';
  import {AnimationService, AssetService, HelpInformationService} from '../../services';
  import {writable} from 'svelte/store';
  import type {Asset} from '../../types/asset';
  import {toasts} from 'svelte-toasts';
  import {fly} from 'svelte/transition';
  import {Field, CloseButton} from '@components';

  import loadingStore from '@/pages/preview/loading-store';
  import {getInstance} from '../../services/animation';
  import {validateFileType} from '../../utils/file';
  import PopoverView from '../../lib/editor/PopoverView/PopoverView.svelte';
  import EditorToggle from '../../lib/editor/EditorToggle/EditorToggle.svelte';
  import HelpSlideOver from '../aside/HelpSlideOver.svelte';
  import {appEvents} from '../../services';
  import type {HelpInformation} from '@/types/settings';
  import editorStore from '../../lib/editor/editor.store';
  import TemplateSelection from '../../lib/editor/TemplateSelection/TemplateSelection.svelte';
  import {createEventDispatcher} from 'svelte';
  import {logError} from '../../utils/logs';

  let fonts = [];
  let media = [];
  let divIds: Array<string> = [];

  let uploading = false;
  export let divId: string = '';

  const mediaService = new MediaService();
  const assetService = new AssetService();
  const uploadProgress = writable({
    uploading: false,
    progressPercent: 0,
    name: ''
  });

  const assets = writable<Array<Asset>>([]);
  let musicItems: Array<any> = [];
  let logoURL = '';
  let logoLoading = true;

  $: onLoadLogo($user.brandKit as BrandKit);
  const onLoadLogo = (brandKit: BrandKit | null) => {
    logoURL = brandKit ? brandKit.logoUrl : '';
    logoLoading = false;
  };

  const onLogoChanged = async (e: CustomEvent) => {
    logoLoading = true;
    await user.updateLogo(e.detail.file);
    await user.getBrandKit();
    logoLoading = false;
    preview.set({}); // trigger an empty preview set.
  };

  const getHelpInformation = async () => {
    let ids = await new HelpInformationService().getHelpInformation();

    divIds = ids.map(id => {
      return id.divId;
    });
  };

  const checkInfoAvailability = (divId: string) => {
    if (divIds.includes(divId)) {
      return divId;
    }

    return '';
  };

  const toggleSidePanel = async (event: CustomEvent) => {
    if (event.detail.divId != '') {
      divId = event.detail.divId;
    }
  };

  const upload = async <T extends UploadActions>(type: T): Promise<UploadPayload[T] | null> => {
    const validTypes = {
      font: [
        'jfproj',
        'ttf',
        'vlw',
        'fnt',
        'woff',
        'fot',
        'pfb',
        'sfd',
        'pfa',
        'otf',
        'woff2',
        'odttf',
        'pf2',
        'pmt',
        'ttc',
        'etx'
      ]
    };

    try {
      const files = await createFileUploadDialog();
      const formData = new FormData();

      for (let i = 0; i < files.length; i++) {
        const file = files.item(i);
        if (validTypes[type] && file) {
          if (validateFileType(file.name, validTypes[type])) {
            formData.append('files', file);
          } else {
            Swal.fire({
              title: 'Invalid file type',
              text: `Please upload a valid  file`,
              icon: 'error'
            });
            return null;
          }
        } else {
          if (file) formData.append('files', file);
        }
      }
      formData.append('type', type);
      uploadProgress.update(store => ({...store, uploading: true}));
      const response = await httpClient.post('/assets', formData, {
        onUploadProgress: progressEvent => {
          const {loaded, total} = progressEvent;
          const progress = (loaded / total) * 100;
          uploadProgress.update(store => ({
            ...store,
            progressPercent: progress
          }));
        }
      });
      uploadProgress.set({
        uploading: false,
        progressPercent: 0,
        name: ''
      });
      const data = response.data;
      return data;
    } catch (err) {
      logError('Error in upload', err);
      Swal.fire({
        titleText: 'Problem with upload',
        text: 'There was a problem with your file. Our service could not successfully process it, if the issue continues to persist, please contact us',
        icon: 'error'
      });
    }
    return null;
  };

  const uploadMusic = async () => {
    const data = await upload('music');
    if (data) {
      fetchAssets();
      toasts.add({
        title: 'Success',
        description: 'Music file successfully uploaded',
        duration: 1500, // 0 or negative to avoid auto-remove
        placement: 'top-left',
        type: 'success',
        theme: 'light'
      });
    }
  };

  const uploadFont = async () => {
    const data = await upload('font');
    if (data) {
      fetchAssets();
      toasts.add({
        title: 'Success',
        description: 'Font successfully uploaded',
        duration: 1500, // 0 or negative to avoid auto-remove
        placement: 'top-left',
        type: 'success',
        theme: 'light'
      });
    }
  };

  const fetchAssets = async () => {
    const data = await assetService.fetchAll();
    assets.set(data);
  };

  onMount(async () => {
    musicItems = await mediaService.fetchAll();
    fetchAssets();
    getHelpInformation();
  });

  const mergeFontArrays = (metadataFonts: Array<any> = [], personalFonts: Array<any> = []) => {
    const filteredFonts = personalFonts.filter(font => font.category.name == 'font') || [];
    let lastId = metadataFonts.length ? metadataFonts[metadataFonts.length - 1].id + 1 : 0;
    const formattedPersonalFonts =
      filteredFonts.map(font => {
        const url: string = font.file.publicUrl;
        const name = font.name.substring(0, url.lastIndexOf('.'));
        const fontface = new FontFace(name, `url('${url}')`);
        fontface
          .load()
          .then(() => {
            try {
              document.fonts.add(fontface);
            } catch (err) {
              console.warn('document font loading failed.', err);
            }
          })
          .catch(err => logError('Problem loading user font', name, url, err));
        return {
          id: font.id + lastId,
          label: name,
          value: url,
          isCustom: true,
          isUserFont: true
        };
      }) || [];
    return [...formattedPersonalFonts, ...metadataFonts];
  };

  const mergeMusicArray = (metadataMusic: Array<any> = [], personalMusic: Array<any> = []) => {
    const filtered = personalMusic.filter(asset => asset.category.name == 'music') || [];
    let lastId = metadataMusic.length ? metadataMusic[metadataMusic.length - 1].id + 1 : 0;
    const formatted =
      filtered.map(music => {
        return {
          id: music.id + lastId,
          label: music.name,
          value: music.file.publicUrl
        };
      }) || [];
    return [...formatted, ...metadataMusic];
  };

  $: fonts = mergeFontArrays($metadata.fonts, $assets);
  $: media = mergeMusicArray($metadata.music, $assets);
  let loading: boolean;
  const unsubscribe = loadingStore.subscribe(store => {
    loading = store;
  });

  function changeTemplate() {
    editorStore.toggleStylebar(false);
    editorStore.setPopoverComponent(TemplateSelection);
  }

  onDestroy(() => {
    unsubscribe();
  });
</script>

{#if !loading}
  <div class="relative flex w-3/12 flex-col gap-6 overflow-y-hidden bg-slate-400 bg-opacity-10">
    <PopoverView />

    <div class="h-full w-full overflow-y-auto">
      <EditorToggle />

      <div class="!p-5">
        <button
          class="flex w-full cursor-pointer items-center rounded-md border border-gray-50 bg-white p-3 shadow-sm hover:bg-gray-100"
          on:click={changeTemplate}
        >
          <span class="gg-sync inline-block text-primary" />
          <span class="inline-block flex-grow text-center">Change template</span>
        </button>
      </div>
      <div class="flex h-full flex-col gap-6 !p-5">
        <!--SHOW SUBTITLES -->
        <Field
          type="toggle"
          stackedLabel={false}
          label="Show Subtitles"
          bind:value={$preview.showSubtitles}
          divId={checkInfoAvailability('subtitle')}
          on:toggle-aside={toggleSidePanel}
        />

        <!-- SERMON TITLE -->
        {#if $preview.checkIfSermonTitleExist}
          <div class="flex flex-col gap-4">
            <Field type="toggle" label="Title" stackedLabel={false} bind:value={$preview.showTitle} />
            <Field type="text" bind:value={$preview.title} />
          </div>
        {/if}

        {#if $preview.checkIfBrandLogoExists}
          <div class="flex flex-col gap-4">
            <Field
              type="toggle"
              label="Brand Logo"
              stackedLabel={false}
              bind:value={$preview.showBrandLogo}
              divId={checkInfoAvailability('logo')}
              on:toggle-aside={toggleSidePanel}
            />
            <Field
              type="image"
              on:image-changed={onLogoChanged}
              bind:value={logoURL}
              divId={checkInfoAvailability('image')}
              bind:loading={logoLoading}
            />
          </div>
        {/if}

        <!-- Dynamic Elements-->
        {#each $preview.extraInformation as info}
          <Field type="text" label={info.label} dynamic={true} key={info.key} value={info.value} />
        {/each}
        <!-- FONTS -->
        <div class="flex flex-col gap-4">
          <div class="flex w-full items-center">
            <span class="font-medium">Font</span>
            <span
              class="ml-auto cursor-pointer rounded-md bg-white px-2 py-1 text-xs font-light shadow-sm hover:bg-gray-100"
              on:click={uploadFont}
            >
              <Icon name="Upload" className="w-3 h-3 inline-block" /> Upload font</span
            >
          </div>
          <div class="flex w-full gap-3">
            <div>
              <Field type="color" bind:value={$preview.fontColor} />
            </div>
            <div class="flex-grow">
              <Field
                isFont={true}
                type="dropdown"
                items={fonts}
                divId={checkInfoAvailability('font')}
                placeholder="Choose a font"
                bind:value={$preview.fontFamily}
              />
            </div>
          </div>
        </div>

        <!-- TEXT ANIMATION -->
        <Field
          type="dropdown"
          label="Text Animation"
          items={$metadata.textAnimations}
          placeholder="Choose an animation"
          divId={checkInfoAvailability('animation')}
          bind:value={$preview.textAnimation}
        />

        <!-- MUSIC -->
        {#if !!$metadata.music}
          <div class="flex w-full items-center">
            <span class="font-medium">Music</span>
            <span
              class="ml-auto cursor-pointer rounded-md bg-white px-2 py-1 text-xs font-light shadow-sm hover:bg-gray-50"
              on:click={uploadMusic}
            >
              <Icon name="Upload" className="w-3 h-3 inline-block" /> Upload Music</span
            >
          </div>
          <Field
            type="dropdown"
            items={media}
            isMusic={true}
            isClearable={true}
            assets={$assets}
            divId={checkInfoAvailability('music')}
            bind:value={$preview.music}
            placeholder="Choose music"
          />
        {/if}

        <Field
          label="Music Volume"
          type="range"
          divId={checkInfoAvailability('music-volume')}
          bind:value={$preview.audioLevel}
        />
        <Field
          hasMarginBottom={true}
          label="Video Volume"
          type="range"
          divId={checkInfoAvailability('video-volume')}
          bind:value={$preview.videoLevel}
        />
      </div>
    </div>

    {#if $uploadProgress.uploading}
      <div
        in:fly={{y: 200, duration: 1000}}
        out:fly={{y: 200, duration: 500}}
        class="h-20 shrink-0 justify-center bg-white p-4 shadow-md"
      >
        <span class="block font-bold">Uploading</span>
        <span class="block">{$uploadProgress.name}</span>
        <div class="mt-2 block h-4 w-full overflow-hidden rounded-md bg-gray-400">
          <div
            class="block h-full bg-gradient-to-r from-primary to-secondary"
            style="width: {$uploadProgress.progressPercent}%"
          />
        </div>
      </div>
    {/if}
  </div>
{:else}
  <div class="relative flex w-3/12 flex-col justify-center gap-6 bg-slate-400 bg-opacity-10 text-center align-middle">
    <Icon name="Spinner" className=" text-gray-400 w-8 h-8 animate-spin block mx-auto" />
  </div>
{/if}

<style lang="postcss">
  ::-webkit-scrollbar {
    @apply w-2 rounded-full bg-slate-100;
  }

  ::-webkit-scrollbar-track {
    @apply rounded-full shadow-gray-500;
  }

  ::-webkit-scrollbar-thumb {
    @apply rounded-full bg-gray-300;
  }

  ::-webkit-scrollbar-thumb:hover {
    @apply rounded-full bg-gray-400;
  }
</style>
